import { ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CarouselPageEvent } from 'primeng/carousel';
import { switchMap, tap } from 'rxjs/operators';
import { ContentCard } from 'src/app/models/content-card';
import { DataService } from 'src/app/services/data.service';
import { SiteService } from 'src/app/services/site.service';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslatablePageBaseComponent } from '../translatable-page-base.component';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent extends TranslatablePageBaseComponent {

	public videoCards: Array<ContentCard> = [];

	public PAGE_SIZE = 1;
	public carouselIndex: number = 0;
	public showCarousel: boolean = true;

	public activeVideo: ContentCard;
	public vimeoId: string = '';
	public heroImageUrl: string = '';
	public hasCustomHero: boolean = false;
	public heroHeadingText: string = '';
	public currentLanguage: string = 'en';
	public textSupportText: string = '';
	public outroText: string = '';
	public enterGroupCodeText: string = '';
	public cta1Text: string = '';
	public cta2Text: string = '';
	public supportText1: string = '';
	public supportText2: string = '';
	public supportText3: string = '';
	public phoneAccessText: string = '';
	public overlayDismissed: boolean = false;

	get eapData(): Record<string, string> {
		return { ...this.content[7], ...this.content[3], ...this.content[8] };
	}

	// Branding image slots (category 9) — empty means the element doesn't render
	get brandingContent(): Record<string, string> {
		return this.content[9] || {};
	}

	// Well-being copy (category 10): employer override, else catalog default,
	// else the pre-migration hardcoded strings (regression fallback)
	wellBeing(key: string): string {
		const fallbacks: Record<string, string> = {
			'Well-Being Preheadline': 'A New Morning for Employee Wellness',
			'Well-Being Headline': 'Your well-being matters.',
			'Well-Being Text': 'When the "good" in "good morning" feels hollow. When persevering seems impossible. When your to-do list is miles long, and getting help is yet another time-consuming task. <span class="notranslate">One Morning</span> has your back. Because life\'s a struggle, but finding help shouldn\'t be.'
		};
		return this.content[10]?.[key] || this.contentDefaults[10]?.[key] || fallbacks[key] || '';
	}

	// When set (e.g. REGISTER TODAY), the "When you need help getting help"
	// section switches to Register mode (plan decision 6.1)
	get registerButtonLabel(): string {
		return this.eapData['Register Button Label'] || '';
	}

	/**
	 * Inline `background-position` for a CUSTOM hero image only.
	 *
	 * The evergreen photo is tuned in SCSS (zoom + position), so returning ''
	 * here leaves those rules untouched. A custom upload has no such tuning, and
	 * this is the employer's own answer to "which part must stay in frame".
	 */
	get heroFocalPosition(): string {
		if (!this.hasCustomHero) { return ''; }
		return this.focalPointToPosition(this.content[6]?.['Home Hero Focal Point']);
	}

	get heroBackgroundImage(): string {
		if (!this.heroImageUrl) return '';
		// aura origin pinned to the bottom-left corner at all times (A5)
		return `radial-gradient(ellipse at bottom left, #FDD26E 0%, #FDD26E 4%, transparent 35%, transparent 100%), url("${this.heroImageUrl}")`;
	}

	private updateHeroBackground(): void {
		const overrideImage = this.content[6]?.['Home Hero Image Override'];
		const imageUrl = (overrideImage && overrideImage.trim()) ? overrideImage : '/assets/images/hero-img.jpg';

		// Set the property directly for template binding
		this.heroImageUrl = imageUrl;
		// The mobile crop is tuned to the evergreen photo specifically (see the
		// hero rules in the SCSS). An employer's own image has its own
		// composition, so that tuning must not apply to it.
		this.hasCustomHero = !!(overrideImage && overrideImage.trim());

		console.log('Hero background updated:', { imageUrl: this.heroImageUrl });
	}

	private updateHeroHeading(): void {
		// Get the current language
		const currentLang = this.translationService.getCurrentLanguage();
		
		// Base text template with notranslate span around employer name
		// "start here." is kept on one line so long employer names break before it
		// rather than splitting the phrase, matching the mockup's 3-line wrap.
		const textTemplate = `Better mornings for <span class="notranslate">${this.employer.employerName}</span> <span class="nowrap">start <em>here</em>.</span>`;
		
		if (currentLang === 'en') {
			// Use original English text
			this.heroHeadingText = textTemplate;
		} else {
			// Request translation for the text with actual employer name
			const items = [{ id: 'home-hero-heading', text: textTemplate }];
			this.translationService.translatePage(
				this.employer.employerID,
				'/home',
				items
			).subscribe(translations => {
				this.heroHeadingText = translations['home-hero-heading'] || textTemplate;
			});
		}
	}

	private updateTextsWithHtml(): void {
    const currentLang = this.translationService.getCurrentLanguage();
    const textSupportTemplate = this.eapData['Text Phone Number'] || '';
    const tollFreeNumber = this.eapData['Toll Free Number'] || '';

    // Build CTA button labels from new CTA Button fields
    const defaultVisitEapText = 'Visit Your <span class="notranslate">EAP</span>';
    const defaultCallEapText = 'Call Your <span class="notranslate">EAP</span>';

    const btn1Type = this.eapData['CTA Button 1 Type'];
    const btn1LabelOverride = this.eapData['CTA Button 1 Label Override'];
    let cta1Template = '';
    if (btn1Type === 'Visit EAP') {
        cta1Template = (btn1LabelOverride?.trim()) ? btn1LabelOverride : defaultVisitEapText;
    } else if (btn1Type === 'Call EAP') {
        cta1Template = (btn1LabelOverride?.trim()) ? btn1LabelOverride : defaultCallEapText;
    }

    const btn2Type = this.eapData['CTA Button 2 Type'];
    const btn2LabelOverride = this.eapData['CTA Button 2 Label Override'];
    let cta2Template = '';
    if (btn2Type === 'Visit EAP') {
        cta2Template = (btn2LabelOverride?.trim()) ? btn2LabelOverride : defaultVisitEapText;
    } else if (btn2Type === 'Call EAP') {
        cta2Template = (btn2LabelOverride?.trim()) ? btn2LabelOverride : defaultCallEapText;
    }

    // Define texts with HTML formatting - these will be sent for translation
    const texts = {
        textSupport: textSupportTemplate,
        outro: 'It gets <i>easier</i>, starting now.',
        enterCode: 'Enter group code',
        cta1: cta1Template,
        cta2: cta2Template,
        support1: 'Get the support you and your family need from your <span class="notranslate">EAP</span> &ndash; at zero cost.',
        support2: 'Our mental health is with us wherever we are. Why pretend it doesn\'t affect us? By facing it head-on, we can embrace the possibility of better.',
        support3: 'Talk it out. Type it out. Text it out. Connect with a counselor in the way that works for you. Confidential support is available 24/7 for you and your entire household &ndash; ready whenever you need it.',
        phoneAccess: tollFreeNumber
            ? `Live, toll-free phone access to licensed, experienced counselors by calling <i class="nowrap">${tollFreeNumber}</i>`
            : ''
    };
    
    if (currentLang === 'en') {
        this.applyTexts(texts);
    } else {
        const items = [
            { id: 'home-outro-text', text: texts.outro },
            { id: 'home-enter-group-code', text: texts.enterCode },
            { id: 'home-support-text1', text: texts.support1 },
            { id: 'home-support-text2', text: texts.support2 },
            { id: 'home-support-text3', text: texts.support3 }
        ];

        // Only add if not empty
        if (texts.textSupport) {
            items.push({ id: 'home-text-support-desc', text: texts.textSupport });
        }

        if (texts.cta1) {
            items.push({ id: 'home-cta1-text', text: texts.cta1 });
        }

        if (texts.cta2) {
            items.push({ id: 'home-cta2-text', text: texts.cta2 });
        }

        if (texts.phoneAccess) {
            items.push({ id: 'home-phone-access-desc', text: texts.phoneAccess });
        }

        this.translationService.translatePage(
            this.employer.employerID,
            '/home',
            items
        ).subscribe(translations => {
            this.applyTexts({
                textSupport: translations['home-text-support-desc'] || texts.textSupport,
                outro: translations['home-outro-text'] || texts.outro,
                enterCode: translations['home-enter-group-code'] || texts.enterCode,
                cta1: translations['home-cta1-text'] || texts.cta1,
                cta2: translations['home-cta2-text'] || texts.cta2,
                support1: translations['home-support-text1'] || texts.support1,
                support2: translations['home-support-text2'] || texts.support2,
                support3: translations['home-support-text3'] || texts.support3,
                phoneAccess: translations['home-phone-access-desc'] || texts.phoneAccess
            });
        });
    }
}

private applyTexts(texts: any): void {
    this.textSupportText = texts.textSupport;
    this.outroText = texts.outro;
    this.enterGroupCodeText = texts.enterCode;
    this.cta1Text = texts.cta1;
    this.cta2Text = texts.cta2;
    this.supportText1 = texts.support1;
    this.supportText2 = texts.support2;
    this.supportText3 = texts.support3;
    this.phoneAccessText = texts.phoneAccess;
}

	private formatPhone(phone: string): string {
		if (!phone) return '';
		// Remove all non-numeric characters
		const cleaned = phone.replace(/\D/g, '');
		// Format as (XXX) XXX-XXXX
		if (cleaned.length === 10) {
			return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
		}
		return phone; // Return original if not 10 digits
	}

	sanitizePhone(phone: string): string {
		if (!phone) return '';
		return phone.replace(/\D/g, '');
	}

	formatContentWithLineBreaks(content: string): string {
		if (!content) return '';
		
		// Split by double line breaks to create paragraphs
		const paragraphs = content.split(/\n\s*\n/);
		
		// Wrap each paragraph in <p class="text"> tags with extra line break for spacing
		const formattedParagraphs = paragraphs
			.map(p => p.trim())
			.filter(p => p.length > 0)
			.map(p => `<p class="text">${p}</p>`)
			.join('<br>');
		
		// Add extra paragraph break at the end for consistent spacing
		return formattedParagraphs + '<br>';
	}

	constructor(
		dataService: DataService,
		private route: ActivatedRoute,
		siteService: SiteService,
		changeDetector: ChangeDetectorRef,
		translationService: TranslationService
	) {
		super(dataService, siteService, changeDetector, translationService);
		
		// Subscribe to language changes
		this.translationService.currentLanguage$.subscribe(lang => {
			this.currentLanguage = lang;
		});
		
		this.route.parent?.parent?.data.subscribe(data => {
			this.path = data['path'] || window.location.hostname.split('.')[0];
			const vimeoId = data['vimeoId'];
			if (vimeoId) {
				this.vimeoId = vimeoId;
				this.playVimeoVideo();
			}
		});
		//Carousel.prototype.onTouchMove = () => { }; //https://stackoverflow.com/a/62581853
	}

	isVimeo(url: string): boolean {
		return /(?:player\.vimeo\.com)/.test(url);
	}

	getVttUrl(videoUrl: string, language: string): string {
		if (!videoUrl) return '';
		
		// Remove query string first
		const baseUrl = videoUrl.split('?')[0];
		
		// Check if URL has a video extension, if so replace it, otherwise just append
		const vttUrl = baseUrl.match(/\.(mp4|webm|mov|avi|mkv|flv|wmv)$/i)
			? baseUrl.replace(/\.(mp4|webm|mov|avi|mkv|flv|wmv)$/i, `-${language}.vtt`)
			: `${baseUrl}-${language}.vtt`;
		console.log(`VTT URL for ${language}:`, vttUrl);
		return vttUrl;
	}
	ngAfterViewChecked(): void {
		const carousels = document.querySelectorAll('.p-carousel-items-container');
	  }
	override ngOnInit(): void {
		super.ngOnInit();
		
		this.route.paramMap.pipe(
			tap(p => {
				this.path = p.get('path') || window.location.hostname.split('.')[0];
				const vimeoId = p.get('vimeoId');
				if (vimeoId) {
					this.vimeoId = vimeoId;
					this.playVimeoVideo();
				}
			}),
			switchMap(p => {
				if (!this.path) {
					throw new Error('Path is required');
				}
				return this.dataService.getEmployerIdFromPath(this.path);
			}),
			switchMap(id => this.dataService.getEmployer(id))
		).subscribe((e) => {
			this.loadEmployerData(e);
		});
		
		this.siteService.onResize.subscribe(size => {
			switch(size) {
				case 's': this.PAGE_SIZE = 1; break;
				case 'm': this.PAGE_SIZE = 2; break;
				case 'l': this.PAGE_SIZE = 3; break;
				case 'xl': this.PAGE_SIZE = 4; break;
			}
			this.showCarousel = false; //trigger refresh of carousel
			setTimeout(() => { this.showCarousel = true; });
		});
	}	

	ngAfterViewInit() {
		
	}

	playVimeoVideo(): void {
		this.activeVideo = new ContentCard({
			title: 'One Morning',
			videoFile: `https://player.vimeo.com/video/${this.vimeoId}`,
			cardContent: ''
		});
		document.body.style.top = `-${window.scrollY}px`;
		document.body.style.position = 'fixed';
		document.body.style.paddingRight = '15px';
	}

	playVideo(card: ContentCard): void {
		this.activeVideo = card;
		document.body.style.top = `-${window.scrollY}px`;
		document.body.style.position = 'fixed';
		document.body.style.paddingRight = '15px';
	}

	closeVideo(): void {
		this.activeVideo = null;
		document.body.style.position = '';
		const scrollY = document.body.style.top;
		document.body.style.top = '';
		document.body.style.paddingRight = '0px';
		window.scrollTo(0, parseInt(scrollY || '0') * -1);
	}

	onPageChange(e: CarouselPageEvent) {
		this.carouselIndex = e.page;
	}

	// Base class abstract method implementations
	
	protected getPagePath(): string {
		return '/home';
	}

	protected getCards(): Array<ContentCard> {
		return this.videoCards;
	}

	protected getCardPrefix(): string {
		return 'video-card';
	}

	protected loadCards(): void {
		this.videoCards = this.employer.employerCards.cardContainers.find(c => c.cardContainerID == 1)?.cards.map(c => new ContentCard(c));
	}

	protected updatePageContent(): void {
		this.updateHeroBackground();
		this.updateHeroHeading();
		this.updateTextsWithHtml();
	}

	protected onLanguageChange(): void {
		this.updateHeroHeading();
		this.updateTextsWithHtml();
	}

	protected applyCardSpecificTranslations(): void {
		// No additional logic needed for home component
	}

	protected collectDynamicContent(items: Array<{ id: string; text: string }>): void {
		super.collectDynamicContent(items);
		// The well-being copy renders the catalog default when the employer has no
		// override; the base collection only covers overrides, so submit the
		// resolved default under the same key for translation. applyDynamicTranslations
		// writes the result back into content[10], which wellBeing() reads first.
		['Well-Being Preheadline', 'Well-Being Headline', 'Well-Being Text'].forEach(key => {
			if (!this.content[10]?.[key]) {
				const value = this.wellBeing(key);
				if (value) {
					items.push({ id: `content-10-${key}`, text: value });
				}
			}
		});
	}
}
